/**
 * Module to define constants for Rudderstack
 *
 */

/**
 * Constants for Rudderstack user marketing preferences
 *
 */
const MARKETING_USER_PREFERENCES = {
  MEDIUM: {
    EMAIL: 'Email',
    SMS: 'SMS',
  },
  PLACEMENT: {
    FOOTER: 'footer',
    PRODUCT_UPDATES_FORM: 'productUpdatesForm',
    SIGNUP_MODULE: 'signupModule',
  },
  TYPE: {
    ALL: 'All',
  },
};

/**
 * Constants for Rudderstack Query String API
 * Rudderstack Doc: https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#query-string-api
 *
 * It was observed that the track event triggered by Rudderstack SDK do not have the 'traits', this is because Rudderstack SDK
 *  triggers the event on the "Page Load" event of the next page, the SDK does not provide a way to trigger the event on the "SDK Ready" event
 *  this missing feature lead us to implement our own Query String API
 *
 * The original params are prefixed with 'a_' from allbirds ie:
 *  'ajs_event' --> 'a_ajs_event'
 *  'ajs_prop_<prop_name>' --> 'a_ajs_prop_<prop_name>'
 *
 */
const QUERYSTRING_API = {
  /**
   * Browser URL max length expressed in characters, this is not a standard limit but a limit observed on the different browsers
   */
  BROWSER_MAX_URL_LENGTH: 2000,

  /**
   * Event name query param
   */
  EVENT_NAME_PARAM: 'a_ajs_event',

  /**
   * Event prop prefix query param
   */
  EVENT_PROP_PREFIX_PARAM: 'a_ajs_prop_',

  /**
   * The object params are prefixed with 'a_ajs_prop_type_object' to be serialized and deserialized by the SDK on the next page load
   *  ie: 'ajs_prop__<prop_name>="a_ajs_prop_type_object${JSON.stringify(value)}"
   */
  PROP_TYPE_OBJECT_PREFIX: 'a_ajs_prop_type_object',
};

/*
 *
 * Constants for Rudderstack track events, used for the "Query String API"
 *
 * This should be aligned with the Tracking Plan https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 */
const TRACK_EVENT_NAMES = {
  CALL_TO_ACTION_CLICKED: 'Call to Action Clicked',
  FOOTER_LINK_CLICKED: 'Footer Link Clicked',
  GLOBAL_BANNER_LINK_CLICKED: 'Global Banner Link Clicked',
  HOMEPAGE_HERO_CLICKED: 'Homepage Hero Clicked',
  HOMEPAGE_MODULE_CLICKED: 'Homepage Module Clicked',
  MINI_NAVIGATION_CLICKED: 'Mini Navigation Clicked',
  NAVIGATION_CLICKED: 'Navigation Clicked',
  PRODUCT_LIST_CLICKED: 'Product Clicked', // This is mapped on Amplitude as 'Product List Clicked'
  PRODUCT_LIST_FEATURE_CLICKED: 'Product List Feature Clicked',
};

/**
 * Constants for Rudderstack collection types
 *
 */
const COLLECTION_TYPES = {
  CUSTOM_COLLECTION: 'CUSTOM_COLLECTION',
  PARENT_COLLECTION: 'PARENT_COLLECTION',
};

/**
 * Constants for Rudderstack collection parent types
 *
 */
const COLLECTION_PARENT_TYPES = {
  HOME: 'Home',
  MENS: 'Mens',
  NO_PARENT: 'no_parent',
  WOMENS: 'Womens',
};

/**
 * Constants for Rudderstack price tier
 *
 */
const PRICE_TIER = { MSRP: 'msrp' };

/**
 * Constant for Navigation track events
 */
const TRACK_NAVIGATION_LEVEL = {
  BACK: 'back',
  GLOBAL: 'global',
  LEVEL_1: 'level_1',
  LEVEL_2: 'level_2',
  LEVEL_3: 'level_3',
};

/**
 * Constants to identify the source of the cart events
 */
const TRACK_CART_SOURCE = {
  CART: 'Cart',
  PDP: 'Pdp',
  QUICK_ADD: 'Quick Add',
  UPSELL: 'Upsell',
};

/**
 * Constants for Rudderstack collection feature type
 *
 */
const TRACK_COLLECTION_FILTER_TYPE = {
  PILL_TOGGLE: {
    KEY: 'gender_toggle',
    RUDDERSTACK_NAME: 'pill toggle',
  },
  SORTING: {
    KEY: 'sorting',
    RUDDERSTACK_NAME: 'sorting',
  },
};

const TRACK_GENERIC_CTA_CLICKED_EVENT = {
  LOCATIONS: {
    CART: 'cart',
    COLLECTION: 'collection',
    NOT_FOUND: '404',
    NOT_FOUND_CATEGORY_ENTRY: '404 category entry',
    PDP: 'pdp',
  },
  TYPES: {
    BUTTON: 'button',
    CARD: 'card',
    ICON: 'icon',
    LINK: 'link',
    PILL_TOGGLE: 'pill toggle',
    SEARCH: 'search',
  },
};

/**
 * Constants for Rudderstack communication links
 *
 */
const TRACK_COMMUNICATION_LINKS = {
  /**
   * Keyword used to open the chat bubble on the "Chat with us" link
   */
  OPEN_CHAT_KEYWORD: 'open-chat',

  /**
   * Prefix used to track the social media links
   */
  SOCIAL_MEDIA_LINKS_TRACKING_NAME_PREFIX: 'Social Icon - ',
};

/**
 * Constants for Rudderstack search source
 */
const TRACK_SEARCH_SOURCE = {
  SEARCH_PAGE: 'search_page',
};

export {
  COLLECTION_PARENT_TYPES,
  COLLECTION_TYPES,
  MARKETING_USER_PREFERENCES,
  PRICE_TIER,
  QUERYSTRING_API,
  TRACK_CART_SOURCE,
  TRACK_COLLECTION_FILTER_TYPE,
  TRACK_COMMUNICATION_LINKS,
  TRACK_EVENT_NAMES,
  TRACK_GENERIC_CTA_CLICKED_EVENT,
  TRACK_NAVIGATION_LEVEL,
  TRACK_SEARCH_SOURCE,
};
